import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import { getWaNotificationLog, getWaNotificationStats } from '../../../services/api';
import { toast } from 'sonner';
import {
  Bell, RefreshCw, MessageCircle, Inbox, Bot, FileText, Zap, Users, Settings,
  CheckCircle, XCircle, Search, X
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Cuentas', path: '/superadmin/whatsapp', icon: MessageCircle },
  { label: 'Inbox', path: '/superadmin/whatsapp/inbox', icon: Inbox },
  { label: 'Auto-respuestas', path: '/superadmin/whatsapp/auto-replies', icon: Bot },
  { label: 'Plantillas', path: '/superadmin/whatsapp/templates', icon: FileText },
  { label: 'Resp. Rapidas', path: '/superadmin/whatsapp/quick-replies', icon: Zap },
  { label: 'Contactos', path: '/superadmin/whatsapp/contacts', icon: Users },
  { label: 'Eventos', path: '/superadmin/whatsapp/event-settings', icon: Settings },
  { label: 'Notificaciones', path: '/superadmin/whatsapp/notifications', icon: Bell },
];

const WhatsAppNotificationLog = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const limit = 50;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filterType) params.notification_type = filterType;
      if (filterPhone) params.phone = filterPhone;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await getWaNotificationLog(params);
      setLogs(res.data?.logs || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch { toast.error('Error cargando log'); }
    finally { setLoading(false); }
  }, [page, filterType, filterPhone, dateFrom, dateTo]);

  const loadStats = useCallback(async () => {
    try {
      const res = await getWaNotificationStats();
      setStats(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadLogs(); loadStats(); }, [loadLogs, loadStats]);

  const totalPages = Math.ceil(total / limit);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Sub-nav */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-slate-200">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = item.path === '/superadmin/whatsapp/notifications';
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  active ? 'bg-green-100 text-green-700 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
              <Bell className="w-5 h-5 text-rose-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Log de Notificaciones</h1>
              <p className="text-sm text-slate-500">Historial de notificaciones WhatsApp programaticas</p>
            </div>
          </div>
          <button onClick={() => { loadLogs(); loadStats(); }}
            className="p-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.sent_today}</p>
              <p className="text-xs text-slate-500 mt-1">Enviados hoy</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <p className="text-2xl font-bold text-red-500">{stats.failed_today}</p>
              <p className="text-xs text-slate-500 mt-1">Fallidos hoy</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{stats.total_sent}</p>
              <p className="text-xs text-slate-500 mt-1">Total enviados</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <p className="text-2xl font-bold text-slate-400">{stats.total_failed}</p>
              <p className="text-xs text-slate-500 mt-1">Total fallidos</p>
            </div>
          </div>
        )}

        {/* By Type */}
        {stats?.by_type && Object.keys(stats.by_type).length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Por tipo</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.by_type).map(([type, counts]) => (
                <div key={type} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                  <span className="text-xs font-medium text-slate-700">{type}</span>
                  <span className="text-xs text-green-600">{counts.sent || 0} ok</span>
                  {(counts.failed || 0) > 0 && <span className="text-xs text-red-500">{counts.failed} fail</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <input value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}
            placeholder="Tipo (ej: order, verification)"
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 w-48" />
          <input value={filterPhone} onChange={e => { setFilterPhone(e.target.value); setPage(1); }}
            placeholder="Telefono"
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 w-40" />
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          {(filterType || filterPhone || dateFrom || dateTo) && (
            <button onClick={() => { setFilterType(''); setFilterPhone(''); setDateFrom(''); setDateTo(''); setPage(1); }}
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
              <X className="w-3 h-3" /> Limpiar
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Cargando...</div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No hay notificaciones</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-slate-600">Fecha</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Tipo</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Telefono</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Template</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Contenido</th>
                    <th className="px-4 py-3 font-medium text-slate-600 text-center">Estado</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {log.created_at ? new Date(log.created_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{log.notification_type}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{log.to_phone}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{log.template_name || '-'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate">{log.content}</td>
                      <td className="px-4 py-3 text-center">
                        {log.status === 'sent' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Enviado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                            <XCircle className="w-3 h-3" /> Fallido
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-red-500 max-w-[150px] truncate">{log.error || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <p className="text-xs text-slate-500">{total} registros</p>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1 text-xs bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-40">Anterior</button>
                <span className="px-3 py-1 text-xs text-slate-600">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="px-3 py-1 text-xs bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-40">Siguiente</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WhatsAppNotificationLog;
